import React, { useState, useEffect, useRef } from 'react';
import { useAdminStore } from '../store/useAdminStore';

function AdminDatabase() {
  const { databaseRegistrations: data, setDatabaseRegistrations: setData, databasePage: currentPage, setDatabasePage: setCurrentPage, adminIdempotencyKey, regenerateAdminIdempotencyKey } = useAdminStore();
  
  const [loading, setLoading] = useState(data.length === 0);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('all');
  const [selectedOlympiads, setSelectedOlympiads] = useState(['all']);
  const [availableOlympiads, setAvailableOlympiads] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const rowsPerPage = 25;

  const [selectedRegistrations, setSelectedRegistrations] = useState([]);

  const handleSelectRegistration = (id) => {
    setSelectedRegistrations(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const handleSelectAllRegistrations = () => {
    if (selectedRegistrations.length === data.length) setSelectedRegistrations([]);
    else setSelectedRegistrations(data.map(r => r.id));
  };

  const handleDeleteRegistration = async (id) => {
    if (!window.confirm("Delete this registration permanently?")) return;
    try {
      const res = await fetch(`/api/admin/registrations/${id}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
          setData(data.filter(r => r.id !== id));
          setSelectedRegistrations(prev => prev.filter(r => r !== id));
        } else {
        const data = await res.json().catch(() => ({}));
        alert(`Delete failed: ${data.error || res.statusText}`);
      }
    } catch { alert("Network error"); } finally { regenerateAdminIdempotencyKey(); }
  };

  const handleAnonymizeRegistration = async (id) => {
    if (!window.confirm("⚠️ IRREVERSIBLE ACTION ⚠️\n\nAre you sure you want to GDPR Anonymize this user? This will cryptographically scrub their PII (Name, Email, Phone) from the database forever while keeping the ledger chain intact. This cannot be undone.")) return;
    try {
      const res = await fetch(`/api/admin/registrations/${id}/anonymize`, {
        method: 'POST',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
          const updatedUser = await res.json();
          setData(data.map(r => r.id === id ? { ...r, ...updatedUser.registration } : r));
          alert("User successfully anonymized.");
        } else {
        const data = await res.json().catch(() => ({}));
        alert(`Anonymization failed: ${data.error || res.statusText}`);
      }
    } catch { alert("Network error"); } finally { regenerateAdminIdempotencyKey(); }
  };

  const handleDeleteSelectedRegistrations = async () => {
    if (selectedRegistrations.length === 0) return;
    if (!window.confirm(`Delete ${selectedRegistrations.length} selected registrations permanently?`)) return;
    try {
      const res = await fetch(`/api/admin/registrations/delete-batch`, {
        method: 'POST',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ ids: selectedRegistrations })
      });
      if (res.ok) {
          setData(data.filter(r => !selectedRegistrations.includes(r.id)));
          setSelectedRegistrations([]);
        } else {
        const data = await res.json().catch(() => ({}));
        alert(`Batch delete failed: ${data.error || res.statusText}`);
      }
    } catch { alert("Network error"); } finally { regenerateAdminIdempotencyKey(); }
  };

  const handleDeleteAllRegistrations = async () => {
    if (!window.confirm("⚠️ EXTREME DANGER ⚠️\n\nWipe ALL registrations permanently?")) return;
    try {
      const res = await fetch(`/api/admin/registrations`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
          setData([]);
          setSelectedRegistrations([]);
        } else {
        const data = await res.json().catch(() => ({}));
        alert(`Delete All failed: ${data.error || res.statusText}`);
      }
    } catch { alert("Network error"); } finally { regenerateAdminIdempotencyKey(); }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Fetch available active olympiads
    fetch('/api/admin/olympiads', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setAvailableOlympiads(data);
      })
      .catch(() => {
        setAvailableOlympiads(['MCO 2026', 'MSO 2026', 'MCOO 2026']);
      });
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect
    setLoading(true);
    const queryParams = new URLSearchParams();
    if (timeframe !== 'all') queryParams.append('timeframe', timeframe);
    if (!selectedOlympiads.includes('all')) queryParams.append('olympiads', selectedOlympiads.join(','));

    fetch(`/api/admin/registrations?${queryParams.toString()}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(res => {
        if (!res.ok) throw new Error('System under heavy load or Unauthorized');
        return res.json();
      })
      .then(json => {
        setData(json);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, selectedOlympiads]);

  const handleOlympiadChange = (e) => {
    const value = e.target.value;
    if (value === 'all') {
      setSelectedOlympiads(['all']);
    } else {
      let newSelection = selectedOlympiads.filter(o => o !== 'all');
      if (newSelection.includes(value)) {
        newSelection = newSelection.filter(o => o !== value);
      } else {
        newSelection.push(value);
      }
      if (newSelection.length === 0) newSelection = ['all'];
      setSelectedOlympiads(newSelection);
    }
  };

  const handleRefund = async (paymentId) => {
    if(!window.confirm(`Issue refund for payment ${paymentId}?`)) return;
    
    try {
      const res = await fetch('/api/admin/refund', {
        method: 'POST',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': useAdminStore.getState().deviceId || '',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ paymentId, amount: 100 }) // Demo amount
      });
      const result = await res.json();
      if(result.success) {
        regenerateAdminIdempotencyKey();
        alert('Refund processed successfully via PhonePe Circuit Breaker.');
      } else {
        alert('Refund failed: ' + result.error);
      }
    } catch {
      alert('Refund failed due to network error.');
    }
  };

  const exportCSV = () => {
    if (data.length === 0) return;

    // Map raw database rows into a clean, logically ordered format
    const cleanData = data.map(row => ({
      'Registration ID': row.id,
      'Full Name': (row.student_name && row.student_name !== 'null') 
                    ? row.student_name 
                    : `${row.first_name !== 'null' && row.first_name ? row.first_name : ''} ${row.last_name !== 'null' && row.last_name ? row.last_name : ''}`.trim() || 'N/A',
      'School': row.school,
      'Class/Grade': row.class_grade,
      'Email': row.email,
      'Phone': row.phone,
      'Olympiad': row.olympiad,
      'Payment Status': row.payment_status,
      'Payment Gateway': row.gateway_name,
      'Gateway Order ID': row.gateway_order_id,
      'Gateway Payment ID': row.gateway_payment_id,
      'UTM Source': row.utm_source,
      'UTM Medium': row.utm_medium,
      'UTM Campaign': row.utm_campaign,
      'Registration Date': row.created_at
    }));

    const headers = Object.keys(cleanData[0]).join(',');
    const rows = cleanData.map(obj => Object.values(obj).map(val => `"${String(val !== null && val !== undefined ? val : '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const csvContent = headers + '\n' + rows;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `registrations_export_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 relative z-50">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Registration Database</h1>
          <p className="text-gray-400 text-sm mt-1">Extreme-Performance Data Grid backed by Dogpile Mutex Locks</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-4">
          <select 
            value={timeframe} 
            onChange={(e) => setTimeframe(e.target.value)}
            className="bg-gray-800 text-white text-sm rounded-md px-3 py-2 border border-gray-700 outline-none focus:border-blue-500"
          >
            <option value="all">All Time</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
          
          <div className="relative" ref={dropdownRef}>
            <button 
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="bg-gray-800 text-white text-sm rounded-md px-3 py-2 border border-gray-700 outline-none focus:border-blue-500 flex items-center gap-2"
            >
              {selectedOlympiads.includes('all') ? 'All Olympiads' : `${selectedOlympiads.length} Selected`}
              <span>{isDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                <label className="flex items-center gap-2 px-4 py-2 hover:bg-gray-700 cursor-pointer rounded-t-lg">
                  <input type="checkbox" value="all" checked={selectedOlympiads.includes('all')} onChange={handleOlympiadChange} />
                  <span className="text-sm text-white">All Olympiads</span>
                </label>
                {availableOlympiads.map(o => (
                  <label key={o} className="flex items-center gap-2 px-4 py-2 hover:bg-gray-700 cursor-pointer">
                    <input type="checkbox" value={o} checked={selectedOlympiads.includes(o)} onChange={handleOlympiadChange} />
                    <span className="text-sm text-white">{o}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button 
              onClick={handleDeleteSelectedRegistrations} 
              disabled={selectedRegistrations.length === 0} 
              className="bg-red-900/50 hover:bg-red-800 text-red-300 font-bold py-2 px-4 rounded border border-red-800 disabled:opacity-50 transition-all">
              Delete Selected
            </button>
            <button 
              onClick={handleDeleteAllRegistrations} 
              disabled={data.length === 0} 
              className="bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-4 rounded transition-all">
              Delete All
            </button>
            <button 
              onClick={exportCSV}
              className="bg-green-600 hover:bg-green-500 text-white font-bold py-2 px-4 rounded shadow-[0_0_15px_rgba(34,197,94,0.4)] transition-all">
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-blue-400 font-mono animate-pulse">
            Querying Database & Building Caches...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500 font-mono">
            {error}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-300">
              <thead className="text-xs text-gray-400 uppercase bg-gray-800 border-b border-gray-700 whitespace-nowrap">
                <tr>
                  <th className="px-6 py-4">
                    <input type="checkbox" checked={selectedRegistrations.length === data.length && data.length > 0} onChange={handleSelectAllRegistrations} />
                  </th>
                  <th className="px-6 py-4">ID</th>
                  <th className="px-6 py-4">Student Name</th>
                  <th className="px-6 py-4">Email</th>
                  <th className="px-6 py-4">Olympiad</th>
                  <th className="px-6 py-4">Payment Status</th>
                  <th className="px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="whitespace-nowrap">
                {data.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500 italic">No registrations found.</td>
                  </tr>
                ) : (
                  data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage).map((row) => (
                    <tr key={row.id} className="bg-gray-900 border-b border-gray-800 hover:bg-gray-800/50 transition-colors">
                      <td className="px-6 py-4">
                        <input type="checkbox" checked={selectedRegistrations.includes(row.id)} onChange={() => handleSelectRegistration(row.id)} />
                      </td>
                      <td className="px-6 py-4 font-mono text-gray-400">{String(row.id).substring(0,8)}</td>
                      <td className="px-6 py-4 font-medium text-white">{row.student_name}</td>
                      <td className="px-6 py-4">{row.email}</td>
                      <td className="px-6 py-4">
                        <span className="bg-blue-900/50 text-blue-400 px-2 py-1 rounded text-xs border border-blue-800/50">
                          {row.olympiad}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs border ${row.payment_status === 'paid' ? 'bg-green-900/50 text-green-400 border-green-800/50' : 'bg-red-900/50 text-red-400 border-red-800/50'}`}>
                          {row.payment_status ? row.payment_status.toUpperCase() : 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="px-6 py-4 flex gap-2">
                        {row.payment_status === 'paid' && row.phonepe_provider_reference_id && (
                          <button 
                            onClick={() => handleRefund(row.phonepe_provider_reference_id)}
                            className="text-red-400 hover:text-red-300 font-semibold text-xs border border-red-900/50 bg-red-900/20 px-3 py-1 rounded transition-colors">
                            Issue Refund
                          </button>
                        )}
                        <button 
                          onClick={() => handleAnonymizeRegistration(row.id)}
                          className="bg-yellow-900/30 hover:bg-yellow-800 text-yellow-400 text-xs px-2 py-1 rounded border border-yellow-900/50">
                          GDPR Anonymize
                        </button>
                        <button 
                          onClick={() => handleDeleteRegistration(row.id)}
                          className="bg-red-900/30 hover:bg-red-800 text-red-400 text-xs px-2 py-1 rounded border border-red-900/50">
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        
        {!loading && !error && data.length > 0 && (
          <div className="bg-gray-950 p-4 border-t border-gray-800 flex justify-between items-center text-sm">
            <span className="text-gray-400">
              Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, data.length)} of {data.length} entries
            </span>
            <div className="flex gap-2">
              <button 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="px-3 py-1 bg-gray-900 border border-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-50 transition-colors">
                Previous
              </button>
              <button 
                disabled={currentPage * rowsPerPage >= data.length}
                onClick={() => setCurrentPage(currentPage + 1)}
                className="px-3 py-1 bg-gray-900 border border-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-50 transition-colors">
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDatabase;

